/*
 * FreshTrends Guided Shopping — Main JS (branching engine)
 * ────────────────────────────────────────────────────────
 * Modal lifecycle, lazy catalog fetch, branching quiz engine, soft-fallback
 * PDP deep-link via metal variant.
 *
 * Depends on window.FT_QUIZ_CONFIG (see guided-shopping-config.js).
 * Each category defines `questions` (object keyed by question id) and an
 * `initial` question id. Each option's `next` points to the next question
 * id, or 'final' to end the quiz and show results.
 *
 * Modal markup must already be in the DOM (rendered by sections/guided-shopping.liquid).
 * Banner triggers carry data-gs-trigger="<category-id>" — clicking opens the modal
 * scoped to that category.
 */
(function () {
  'use strict';

  var CFG = window.FT_QUIZ_CONFIG || {};
  if (!Object.keys(CFG).length) {
    console.warn('[guided-shopping] FT_QUIZ_CONFIG is empty — config asset failed to load');
    return;
  }

  // ─── DOM refs ──────────────────────────────────────────────────────
  var modal       = document.getElementById('gs-modal');
  var closeBtn       = document.getElementById('gs-modal-close');
  var quizTitleEl    = document.getElementById('gs-quiz-title');
  var quizSubEl      = document.getElementById('gs-quiz-subtitle');
  var stepsWrap      = document.getElementById('gs-steps');
  var progressEl     = document.getElementById('gs-progress');
  var navEl          = document.getElementById('gs-nav');
  var backBtn        = document.getElementById('gs-back');
  var resultsEl      = document.getElementById('gs-results');
  var resultsBackBtn = document.getElementById('gs-results-back');
  var gridEl         = document.getElementById('gs-grid');
  var countEl        = document.getElementById('gs-count');
  var restartBtn     = document.getElementById('gs-restart');
  var helixName   = document.getElementById('gs-helix-name');
  var helixRole   = document.getElementById('gs-helix-role');
  var helixCard   = document.getElementById('gs-helix-card');
  var helixBarBtn = document.getElementById('gs-helix-bar-toggle');
  var helixBarPanel = document.getElementById('gs-helix-bar-panel');
  var helixBar    = document.getElementById('gs-helix-bar');

  if (!modal) {
    console.warn('[guided-shopping] modal DOM not found on this page');
    return;
  }

  // ─── analytics ─────────────────────────────────────────────────────
  // Two parallel delivery paths, both wrapped so an ad-blocker or missing
  // dataLayer can never break the quiz itself (Ximena, 2026-08-13):
  //
  // 1. A GTM-style { event: ... } push onto window.dataLayer — inert until/
  //    unless a GTM Custom Event trigger + tag is added for the event name.
  //    Kept so Tom can route these to non-GA4 tags later without theme edits.
  // 2. A DIRECT gtag('event') send to the site's GA4 property (G-H19X33G4LF,
  //    the one GA4 config inside container GTM-WNXL94Q) — this is what makes
  //    the events actually appear in GA4 with no GTM changes required.
  //    gtag.js is loaded lazily on the first quiz event (never for shoppers
  //    who don't touch the quiz) with send_page_view:false, so it adds no
  //    page_view/session double-counting alongside GTM's own GA4 config.
  //    NOTE FOR GTM WORK: do NOT also add GA4 event tags for these dataLayer
  //    events — that would double-count them (Paul, 2026-08-16).
  var GS_GA4_ID = 'G-H19X33G4LF';
  var gsGtagInit = false;
  function ensureGtag() {
    if (gsGtagInit) return;
    gsGtagInit = true;
    window.dataLayer = window.dataLayer || [];
    if (typeof window.gtag !== 'function') {
      window.gtag = function () { window.dataLayer.push(arguments); };
    }
    if (!document.querySelector('script[src*="googletagmanager.com/gtag/js"]')) {
      var s = document.createElement('script');
      s.async = true;
      s.src = 'https://www.googletagmanager.com/gtag/js?id=' + GS_GA4_ID;
      document.head.appendChild(s);
    }
    window.gtag('js', new Date());
    window.gtag('config', GS_GA4_ID, { send_page_view: false });
  }
  function pushQuizEvent(eventName, extra) {
    try {
      window.dataLayer = window.dataLayer || [];
      var payload = { event: eventName };
      if (extra) {
        Object.keys(extra).forEach(function (k) { payload[k] = extra[k]; });
      }
      window.dataLayer.push(payload);
      ensureGtag();
      window.gtag('event', eventName, extra || {});
    } catch (e) { /* never let analytics break the quiz */ }
  }

  // ─── catalog cache ─────────────────────────────────────────────────
  // Each entry is { products: [...], meta: { handle, filters, exclude } | null }
  var catalogCache = {};
  var catalogPending = {};

  // Cached by catalog URL (not category id) so a category that swaps its pool
  // mid-quiz — the ear placement question loads a per-piercing collection —
  // gets its own cache entry instead of colliding on the category id.
  // When the storefront is being viewed through a theme preview (Shopify adds
  // preview_theme_id / pb to the URL), sub-requests like the catalog fetch must
  // carry those params too — otherwise they render on the PUBLISHED (main) theme
  // instead of the previewed one, so the quiz would silently test main's catalog
  // emitter while running the previewed theme's JS. Cache stays keyed on the
  // clean url; only the actual fetch gets the preview params appended.
  function withPreviewParams(url) {
    try {
      var here = new URLSearchParams(window.location.search);
      var carry = '';
      ['preview_theme_id', 'pb'].forEach(function (k) {
        if (here.has(k)) carry += (carry ? '&' : '') + k + '=' + encodeURIComponent(here.get(k));
      });
      if (!carry) return url;
      return url + (url.indexOf('?') === -1 ? '?' : '&') + carry;
    } catch (e) { return url; }
  }

  function fetchCatalogUrl(url) {
    if (!url) return Promise.resolve({ products: [], meta: null });
    if (catalogCache[url]) return Promise.resolve(catalogCache[url]);
    if (catalogPending[url]) return catalogPending[url];

    catalogPending[url] = fetch(withPreviewParams(url), { credentials: 'same-origin' })
      .then(function (r) {
        if (!r.ok) throw new Error('Catalog fetch failed: ' + r.status);
        return r.text();
      })
      .then(function (txt) {
        // Parse optional collection metadata (filter list + exclude list)
        var metaMatch = txt.match(/<script[^>]*id=["']ft-quiz-catalog-meta["'][^>]*>([\s\S]*?)<\/script>/);
        var meta = null;
        if (metaMatch) { try { meta = JSON.parse(metaMatch[1]); } catch (e) {} }

        // Parse products array
        var pm = txt.match(/<script[^>]*id=["']ft-quiz-catalog["'][^>]*>([\s\S]*?)<\/script>/);
        var products = JSON.parse(pm ? pm[1] : txt);

        var result = { products: products, meta: meta };
        catalogCache[url] = result;
        delete catalogPending[url];
        return result;
      })
      .catch(function (err) {
        delete catalogPending[url];
        console.error('[guided-shopping] catalog load error', err);
        throw err;
      });
    return catalogPending[url];
  }

  // A failed catalog fetch (transient network blip, or one of the draft
  // theme's slow ~60s renders timing out) used to leave state.catalog stuck
  // at null forever — refreshOptions has no way to tell "still loading" from
  // "gave up", so every structural question reached after the first stayed
  // on "Loading options…" permanently (Ximena, 2026-08-11: "still happening
  // frequently"). Retry a couple of times before actually giving up.
  function fetchCatalogUrlRetrying(url, attemptsLeft) {
    if (attemptsLeft === undefined) attemptsLeft = 2;
    return fetchCatalogUrl(url).catch(function (err) {
      if (attemptsLeft <= 0) throw err;
      return new Promise(function (resolve) { setTimeout(resolve, 1500); })
        .then(function () { return fetchCatalogUrlRetrying(url, attemptsLeft - 1); });
    });
  }

  function loadCatalog(category) {
    return fetchCatalogUrlRetrying(category && category.catalogUrl);
  }

  // ─── quiz state ────────────────────────────────────────────────────
  // path: array of {id, key, value} for each answered question, in order.
  //       Used for back navigation and soft-fallback ordering.
  // answers: { filter_key → value } derived from path.
  var state = {
    category: null,
    currentId: null,
    path: [],
    answers: {},
    answerOrder: [],
    catalog: null,       // products array
    collectionMeta: null,// { handle, filters, exclude } from catalog-meta tag
    catalogPromise: null // pending pool fetch, when a placement swaps the pool
  };

  function resetState() {
    state.category = null;
    state.currentId = null;
    state.path = [];
    state.answers = {};
    state.previousCategory = null;   // full reset (modal close) — clear routing history
    state.answerOrder = [];
    state.catalog = null;
    state.collectionMeta = null;
    state.catalogPromise = null;
  }

  // ─── rendering ─────────────────────────────────────────────────────
  function fmtPrice(cents) {
    return '$' + (cents / 100).toLocaleString('en-US', { maximumFractionDigits: 0 });
  }

  // Auto-generate SVG illustration for gauge/length/diameter/form/type-picker.
  // Returns SVG string or null. Uses currentColor — CSS drives fill/stroke colour.
  function makeVisualSvg(qKey, val) {
    var k = qKey, v = val;

    // ── Piercing-area illustrations for the "What type?" category picker ──────
    if (k === 'piercing_type') {
      var typeIllus = {
        nose:
          '<svg viewBox="0 0 52 48" xmlns="http://www.w3.org/2000/svg">' +
            // Nose tip outline
            '<path d="M26 5 Q14 16 14 28 Q14 37 20 41 Q26 44 32 41 Q38 37 38 28 Q38 16 26 5Z" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linejoin="round"/>' +
            // Right nostril ring
            '<circle cx="36" cy="39" r="5.5" fill="none" stroke="currentColor" stroke-width="2.2"/>' +
          '</svg>',
        ear:
          '<svg viewBox="0 0 36 54" xmlns="http://www.w3.org/2000/svg">' +
            // Outer ear
            '<path d="M18 4 C28 4 34 14 34 26 C34 38 28 47 18 49 C12 49 8 45 8 41 L8 39 C8 35 12 33 16 35 C18 36 20 35 20 31 C20 25 14 19 12 13 C10 8 12 4 18 4Z" fill="none" stroke="currentColor" stroke-width="2"/>' +
            // Lobe stud
            '<circle cx="11" cy="45" r="4" fill="currentColor"/>' +
          '</svg>',
        belly:
          '<svg viewBox="0 0 64 44" xmlns="http://www.w3.org/2000/svg">' +
            // Banana curved barbell — the iconic navel shape
            '<path d="M10 37 C18 8 46 8 54 37" fill="none" stroke="currentColor" stroke-width="3.5" stroke-linecap="round"/>' +
            '<circle cx="10" cy="37" r="6.5" fill="currentColor"/>' +
            '<circle cx="54" cy="37" r="9.5" fill="currentColor"/>' +
          '</svg>',
        lip:
          '<svg viewBox="0 0 52 38" xmlns="http://www.w3.org/2000/svg">' +
            // Upper lip — Cupid's bow
            '<path d="M4 18 C4 11 12 8 18 12 C20 14 22 12 26 12 C30 12 32 14 34 12 C40 8 48 11 48 18" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round"/>' +
            // Lower lip
            '<path d="M4 18 Q26 33 48 18" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round"/>' +
            // Ring at bottom centre
            '<circle cx="26" cy="36" r="5" fill="none" stroke="currentColor" stroke-width="2.2"/>' +
          '</svg>',
        // ── Lip sub-piercing placement markers — reuse the same lip outline,
        // just move the dot to where that specific piercing sits. ──────────
        labret:
          '<svg viewBox="0 0 52 38" xmlns="http://www.w3.org/2000/svg">' +
            '<path d="M4 18 C4 11 12 8 18 12 C20 14 22 12 26 12 C30 12 32 14 34 12 C40 8 48 11 48 18" fill="none" stroke="currentColor" stroke-width="2" opacity="0.55" stroke-linecap="round"/>' +
            '<path d="M4 18 Q26 33 48 18" fill="none" stroke="currentColor" stroke-width="2" opacity="0.55" stroke-linecap="round"/>' +
            // Below the lower lip, centered — classic labret spot
            '<circle cx="26" cy="30" r="4.5" fill="currentColor"/>' +
          '</svg>',
        side_labret:
          '<svg viewBox="0 0 52 38" xmlns="http://www.w3.org/2000/svg">' +
            '<path d="M4 18 C4 11 12 8 18 12 C20 14 22 12 26 12 C30 12 32 14 34 12 C40 8 48 11 48 18" fill="none" stroke="currentColor" stroke-width="2" opacity="0.55" stroke-linecap="round"/>' +
            '<path d="M4 18 Q26 33 48 18" fill="none" stroke="currentColor" stroke-width="2" opacity="0.55" stroke-linecap="round"/>' +
            // Below the lower lip, off to the side
            '<circle cx="14" cy="27" r="4.5" fill="currentColor"/>' +
          '</svg>',
        medusa:
          '<svg viewBox="0 0 52 38" xmlns="http://www.w3.org/2000/svg">' +
            '<path d="M4 18 C4 11 12 8 18 12 C20 14 22 12 26 12 C30 12 32 14 34 12 C40 8 48 11 48 18" fill="none" stroke="currentColor" stroke-width="2" opacity="0.55" stroke-linecap="round"/>' +
            '<path d="M4 18 Q26 33 48 18" fill="none" stroke="currentColor" stroke-width="2" opacity="0.55" stroke-linecap="round"/>' +
            // Above the upper lip, centered on the philtrum — Medusa's own spot
            '<circle cx="26" cy="8" r="4.5" fill="currentColor"/>' +
          '</svg>',
        madonna_monroe:
          '<svg viewBox="0 0 52 38" xmlns="http://www.w3.org/2000/svg">' +
            '<path d="M4 18 C4 11 12 8 18 12 C20 14 22 12 26 12 C30 12 32 14 34 12 C40 8 48 11 48 18" fill="none" stroke="currentColor" stroke-width="2" opacity="0.55" stroke-linecap="round"/>' +
            '<path d="M4 18 Q26 33 48 18" fill="none" stroke="currentColor" stroke-width="2" opacity="0.55" stroke-linecap="round"/>' +
            // Above the upper lip, off-center on both sides — Madonna and Monroe
            // are the mirrored pair, so both spots are marked.
            '<circle cx="14" cy="11" r="4" fill="currentColor"/>' +
            '<circle cx="38" cy="11" r="4" fill="currentColor"/>' +
          '</svg>',
        ashley:
          '<svg viewBox="0 0 52 38" xmlns="http://www.w3.org/2000/svg">' +
            '<path d="M4 18 C4 11 12 8 18 12 C20 14 22 12 26 12 C30 12 32 14 34 12 C40 8 48 11 48 18" fill="none" stroke="currentColor" stroke-width="2" opacity="0.55" stroke-linecap="round"/>' +
            '<path d="M4 18 Q26 33 48 18" fill="none" stroke="currentColor" stroke-width="2" opacity="0.55" stroke-linecap="round"/>' +
            // Through the lip line itself, centered — Ashley goes through the tissue
            '<circle cx="26" cy="18" r="4.5" fill="currentColor"/>' +
          '</svg>',
        eyebrow:
          '<svg viewBox="0 0 60 40" xmlns="http://www.w3.org/2000/svg">' +
            // Eyebrow arch (subtle)
            '<path d="M4 32 C14 8 46 8 56 32" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" opacity="0.3"/>' +
            // Curved barbell
            '<path d="M14 24 C20 12 40 10 48 18" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round"/>' +
            '<circle cx="14" cy="24" r="5.5" fill="currentColor"/>' +
            '<circle cx="48" cy="18" r="5.5" fill="currentColor"/>' +
          '</svg>',
        nipple:
          '<svg viewBox="0 0 72 28" xmlns="http://www.w3.org/2000/svg">' +
            // Straight barbell — tasteful abstract
            '<circle cx="10" cy="14" r="9" fill="currentColor"/>' +
            '<rect x="19" y="11" width="34" height="6" rx="3" fill="currentColor"/>' +
            '<circle cx="62" cy="14" r="9" fill="currentColor"/>' +
          '</svg>',
        septum:
          '<svg viewBox="0 0 52 44" xmlns="http://www.w3.org/2000/svg">' +
            // Left nostril curve
            '<path d="M4 28 Q10 18 18 22" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round"/>' +
            // Right nostril curve
            '<path d="M48 28 Q42 18 34 22" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round"/>' +
            // Horseshoe ring
            '<path d="M18 40 A8 8 0 0 1 34 40" fill="none" stroke="currentColor" stroke-width="4" stroke-linecap="round"/>' +
            '<circle cx="18" cy="40" r="5" fill="currentColor"/>' +
            '<circle cx="34" cy="40" r="5" fill="currentColor"/>' +
          '</svg>'
      };
      return typeIllus[v] || null;
    }

    // ── Gauge cross-section: exaggerated scale so 20G vs 14G is obvious ───────
    var isGauge = k === 'gauge' || k.indexOf('gauge') !== -1;
    if (isGauge) {
      var gr = { '12G': 19, '14G': 15, '16G': 11, '18G': 7.5, '20G': 5, '22G': 3.5 }[v];
      if (gr) return (
        '<svg viewBox="0 0 48 48" xmlns="http://www.w3.org/2000/svg">' +
          '<circle cx="24" cy="24" r="22" fill="none" stroke="currentColor" stroke-width="0.75" stroke-dasharray="3 2" opacity="0.18"/>' +
          '<circle cx="24" cy="24" r="' + gr + '" fill="currentColor"/>' +
        '</svg>'
      );
    }

    // ── Barbell / post length ────────────────────────────────────────────────
    // Quadratic scale (bl ∝ mm²) so adjacent values spread apart visually.
    // For nipple 9/16/5/8/3/4 → bars 43/52/72 (long is ~1.7× short). ✓
    // For belly 3/8/7/16/1/2 → bars 22/28/35 (long is ~1.6× short). ✓
    if (k === 'post_length') {
      var lens = { '3/16': 10, '1/4': 13, '5/16': 17, '3/8': 22, '7/16': 28, '1/2': 35, '9/16': 43, '5/8': 52, '3/4': 72 };
      var bl = lens[v];
      if (bl) {
        var bx1 = Math.round((80 - bl) / 2);
        var bx2 = bx1 + bl;
        var bw = bl - 14; // bar rect width (minus 2×r)
        return (
          '<svg viewBox="0 0 80 32" xmlns="http://www.w3.org/2000/svg">' +
            '<circle cx="' + bx1 + '" cy="16" r="7" fill="currentColor"/>' +
            (bw > 0 ? '<rect x="' + (bx1 + 7) + '" y="13" width="' + bw + '" height="6" rx="3" fill="currentColor"/>' : '') +
            '<circle cx="' + bx2 + '" cy="16" r="7" fill="currentColor"/>' +
          '</svg>'
        );
      }
    }

    // ── Gem size: top-down stone-in-setting view — tiny is a dot, bling fills the card ──
    if (k === 'size') {
      var smap = { tiny: [6, 8.5], standard: [11, 13.5], bling: [17, 20] };
      var sg = smap[v];
      if (sg) {
        var sr = sg[0], rim = sg[1], cx = 21, cy = 21;
        return (
          '<svg viewBox="0 0 42 42" xmlns="http://www.w3.org/2000/svg">' +
            // Bezel / setting rim
            '<circle cx="' + cx + '" cy="' + cy + '" r="' + rim + '" fill="none" stroke="currentColor" stroke-width="1.5"/>' +
            // Stone body
            '<circle cx="' + cx + '" cy="' + cy + '" r="' + sr + '" fill="currentColor"/>' +
            // Facet cross lines
            '<line x1="' + cx + '" y1="' + (cy-sr) + '" x2="' + cx + '" y2="' + (cy+sr) + '" stroke="white" stroke-width="0.8" opacity="0.28"/>' +
            '<line x1="' + (cx-sr) + '" y1="' + cy + '" x2="' + (cx+sr) + '" y2="' + cy + '" stroke="white" stroke-width="0.8" opacity="0.28"/>' +
            // Table circle (inner ring — suggests faceted cut)
            '<circle cx="' + cx + '" cy="' + cy + '" r="' + Math.round(sr * 0.5) + '" fill="none" stroke="white" stroke-width="0.7" opacity="0.22"/>' +
          '</svg>'
        );
      }
    }

    // ── Nose ring backing: side-profile diagram of each mechanism ────────────
    if (k === 'backing') {
      var bstone = '<circle cx="18" cy="10" r="9" fill="currentColor"/>';
      if (v === 'flatback') return (
        '<svg viewBox="0 0 36 52" xmlns="http://www.w3.org/2000/svg">' + bstone +
          // Straight post
          '<rect x="15" y="19" width="6" height="18" rx="3" fill="currentColor"/>' +
          // Flat disc at end (sits flush inside nostril)
          '<ellipse cx="18" cy="39" rx="9" ry="4" fill="currentColor" opacity="0.55"/>' +
        '</svg>'
      );
      if (v === 'twist') return (
        '<svg viewBox="0 0 36 52" xmlns="http://www.w3.org/2000/svg">' + bstone +
          // Corkscrew/screw path
          '<path d="M18 19 C25 23 11 28 18 33 C25 38 11 43 18 47" fill="none" stroke="currentColor" stroke-width="5" stroke-linecap="round"/>' +
        '</svg>'
      );
      if (v === 'bone') return (
        '<svg viewBox="0 0 36 52" xmlns="http://www.w3.org/2000/svg">' + bstone +
          // Straight post
          '<rect x="15" y="19" width="6" height="18" rx="3" fill="currentColor"/>' +
          // Small end ball that holds it in place
          '<circle cx="18" cy="40" r="5.5" fill="currentColor"/>' +
        '</svg>'
      );
      if (v === 'l-post') return (
        '<svg viewBox="0 0 36 52" xmlns="http://www.w3.org/2000/svg">' + bstone +
          // Vertical part
          '<rect x="15" y="19" width="6" height="15" rx="3" fill="currentColor"/>' +
          // Horizontal L-arm (the bend that sits under the nose)
          '<rect x="7" y="29" width="14" height="6" rx="3" fill="currentColor"/>' +
        '</svg>'
      );
    }

    // ── Hoop diameter: variable radius AND stroke weight for clear difference ──
    if (k === 'diameter') {
      // [radius, stroke-width] pairs — bigger ring = bolder stroke too
      var dmap = {
        '3/16': [7,  2],  '1/4':  [9,  2],  '5/16': [12, 2],
        '3/8':  [17, 2.5],'7/16': [22, 3],  '1/2':  [26, 3.5],
        '9/16': [28, 4],  '5/8':  [29, 4.5],'3/4':  [29.5, 5]
      };
      var dm = dmap[v];
      if (dm) return (
        '<svg viewBox="0 0 60 60" xmlns="http://www.w3.org/2000/svg">' +
          '<circle cx="30" cy="30" r="' + dm[0] + '" fill="none" stroke="currentColor" stroke-width="' + dm[1] + '"/>' +
        '</svg>'
      );
    }

    // ── Form / style (silhouettes of each jewelry shape) ─────────────────────
    var isForm = k === 'style' || k.indexOf('form') !== -1;
    if (isForm) {
      if (v === 'hoop') return (
        '<svg viewBox="0 0 52 52" xmlns="http://www.w3.org/2000/svg">' +
          '<circle cx="26" cy="26" r="21" fill="none" stroke="currentColor" stroke-width="4.5"/>' +
        '</svg>'
      );
      if (v === 'barbell') return (
        '<svg viewBox="0 0 80 30" xmlns="http://www.w3.org/2000/svg">' +
          '<circle cx="9" cy="15" r="8" fill="currentColor"/>' +
          '<rect x="17" y="12" width="46" height="6" rx="3" fill="currentColor"/>' +
          '<circle cx="71" cy="15" r="8" fill="currentColor"/>' +
        '</svg>'
      );
      if (v === 'labret') return (
        '<svg viewBox="0 0 40 50" xmlns="http://www.w3.org/2000/svg">' +
          '<circle cx="20" cy="11" r="10" fill="currentColor"/>' +
          '<rect x="17" y="21" width="6" height="18" rx="3" fill="currentColor"/>' +
          '<ellipse cx="20" cy="42" rx="11" ry="5" fill="currentColor" opacity="0.5"/>' +
        '</svg>'
      );
      if (v === 'retainer') return (
        '<svg viewBox="0 0 52 36" xmlns="http://www.w3.org/2000/svg">' +
          '<path d="M8 34 A22 22 0 0 1 44 34" fill="none" stroke="currentColor" stroke-width="4.5" stroke-linecap="round" opacity="0.4"/>' +
          '<circle cx="8" cy="34" r="5" fill="currentColor" opacity="0.4"/>' +
          '<circle cx="44" cy="34" r="5" fill="currentColor" opacity="0.4"/>' +
        '</svg>'
      );
      if (v === 'stud' && k === 'belly_form') return (
        '<svg viewBox="0 0 72 40" xmlns="http://www.w3.org/2000/svg">' +
          '<path d="M10 34 C26 10 46 10 62 34" fill="none" stroke="currentColor" stroke-width="4.5" stroke-linecap="round"/>' +
          '<circle cx="10" cy="34" r="7" fill="currentColor"/>' +
          '<circle cx="62" cy="34" r="7" fill="currentColor"/>' +
        '</svg>'
      );
      // Eyebrow's "Curved barbell" tile — eyebrow's question key is the
      // generic 'form' (shared with lobe/nose/etc, unlike belly's dedicated
      // 'belly_form'), so it needs a value distinct from plain 'stud' to be
      // targeted here at all; see Q_eb_form's `eyebrow_curved` value.
      if (v === 'eyebrow_curved') return (
        '<svg viewBox="0 0 72 40" xmlns="http://www.w3.org/2000/svg">' +
          '<path d="M10 34 C26 10 46 10 62 34" fill="none" stroke="currentColor" stroke-width="4.5" stroke-linecap="round"/>' +
          '<circle cx="10" cy="34" r="7" fill="currentColor"/>' +
          '<circle cx="62" cy="34" r="7" fill="currentColor"/>' +
        '</svg>'
      );
      // Rook/daith/snug "Curved bar" — a tighter hook than the shallow belly/
      // eyebrow banana arc, matching the actual silhouette of a small curved
      // barbell (per Ximena's reference sketch, 2026-08-11).
      if (v === 'curved_bar') return (
        '<svg viewBox="0 0 52 52" xmlns="http://www.w3.org/2000/svg">' +
          '<path d="M14 42 A20 20 0 1 1 40 16" fill="none" stroke="currentColor" stroke-width="4.5" stroke-linecap="round"/>' +
          '<circle cx="14" cy="42" r="7" fill="currentColor"/>' +
          '<circle cx="40" cy="16" r="7" fill="currentColor"/>' +
        '</svg>'
      );
      if (v === 'stud') return (
        '<svg viewBox="0 0 40 52" xmlns="http://www.w3.org/2000/svg">' +
          '<circle cx="20" cy="12" r="10" fill="currentColor"/>' +
          '<rect x="17" y="22" width="6" height="22" rx="3" fill="currentColor"/>' +
        '</svg>'
      );
    }

    return null;
  }

  function buildStepDOM(question) {
    var el = document.createElement('div');
    el.className = 'gs-step is-active';
    el.dataset.q = question.key;
    el.dataset.qid = question.id;

    var h = document.createElement('h3');
    h.className = 'gs-q-label';
    // question.question can be a function of state.answers — same pattern
    // as question.options — so a shared question (e.g. Q_cart_form, reused
    // by Helix/Forward Helix/Tragus/Anti-tragus) can name-check which
    // specific placement the shopper picked instead of generic copy.
    h.textContent = typeof question.question === 'function'
      ? question.question(state.answers)
      : question.question;
    el.appendChild(h);

    if (question.hint) {
      var hint = document.createElement('p');
      hint.className = 'gs-q-hint';
      hint.textContent = question.hint;
      el.appendChild(hint);
    }

    var opts = document.createElement('div');
    opts.className = 'gs-opts cols-' + (question.cols || 4);
    // options can be an array OR a function of state.answers — the function
    // form lets a step's option list depend on an earlier answer (used by
    // belly where floating vs classic have different length ranges + labels).
    var optionsArr = typeof question.options === 'function'
      ? (question.options(state.answers) || [])
      : question.options;
    optionsArr.forEach(function (o) {
      var b = document.createElement('div');
      b.className = 'gs-opt';
      b.dataset.value = o.value;
      b.dataset.next = o.next;
      // Live-hiding (refreshOptions) needs to hypothesize this option's real
      // effect on state.answers, same as onAnswer does — an option whose
      // value is remapped via `writes` (e.g. curved_bar -> form:'stud')
      // would otherwise get checked against catalog data under its raw,
      // never-real `value`, always computing 0 matches and vanishing.
      if (o.writes) b.dataset.writes = JSON.stringify(o.writes);

      // Image URL takes priority (uploaded icons); then auto-generated SVG; then text icon
      if (o.img) {
        b.classList.add('gs-opt--img');
        var imgEl = document.createElement('img');
        imgEl.className = 'gs-opt-img';
        // Line-art icons (default) get a brown tint filter so they match
        // the theme; photo/full-color icons opt out via `img_photo: true`.
        if (o.img_photo) imgEl.classList.add('gs-opt-img--photo');
        imgEl.src = o.img;
        imgEl.alt = o.label;
        imgEl.loading = 'lazy';
        b.appendChild(imgEl);
      } else {
        var svgStr = makeVisualSvg(question.key, o.value);
        if (svgStr) {
          b.classList.add('gs-opt--visual');
          var vis = document.createElement('span');
          vis.className = 'gs-opt-visual';
          vis.innerHTML = svgStr;
          b.appendChild(vis);
        } else if (o.icon) {
          var ic = document.createElement('span');
          ic.className = 'gs-opt-icon';
          ic.textContent = o.icon;
          b.appendChild(ic);
        }
      }

      var lbl = document.createElement('span');
      lbl.className = 'gs-opt-label';
      lbl.textContent = o.label;
      b.appendChild(lbl);

      if (o.desc) {
        var d = document.createElement('span');
        d.className = 'gs-opt-desc';
        d.textContent = o.desc;
        b.appendChild(d);
      }

      b.addEventListener('click', function () {
        onAnswer(question, o, b);
      });

      opts.appendChild(b);
    });

    // "Not sure?" escape hatch — gauge and length/diameter are the two
    // questions shoppers are most likely to not know off the top of their
    // head (Ximena, 2026-08-12). Every category with a `defaults` object
    // already carries a sensible standard value for exactly this; it was
    // only ever used internally as a fallback, never offered to the
    // shopper. Only applies where the option list is a static array (every
    // current gauge/dimension question) — skips categories with no
    // `defaults[key]` (e.g. belly, which doesn't ask gauge and whose
    // dimension range depends on belly_form) rather than guess a default
    // that isn't declared.
    if ((question.key === 'gauge' || question.key === 'dimension') &&
        state.category.defaults && state.category.defaults[question.key] !== undefined &&
        typeof question.options !== 'function' && optionsArr.length) {
      var notSureOpt = {
        value: state.category.defaults[question.key],
        label: 'Not sure?',
        desc: 'Use the standard size',
        next: optionsArr[0].next
      };
      var nb = document.createElement('div');
      nb.className = 'gs-opt gs-opt--notsure';
      nb.dataset.value = notSureOpt.value;
      nb.dataset.next = notSureOpt.next;
      var nlbl = document.createElement('span');
      nlbl.className = 'gs-opt-label';
      nlbl.textContent = notSureOpt.label;
      nb.appendChild(nlbl);
      var ndesc = document.createElement('span');
      ndesc.className = 'gs-opt-desc';
      ndesc.textContent = notSureOpt.desc;
      nb.appendChild(ndesc);
      nb.addEventListener('click', function () { onAnswer(question, notSureOpt, nb); });
      opts.appendChild(nb);
    }

    el.appendChild(opts);

    return el;
  }

  // Progress dots show one filled dot per answered question + one pulsing
  // dot for the current question. We don't know the total path length
  // upfront because the tree branches — so we don't render an "of N".
  function renderProgress() {
    if (!progressEl) return;
    progressEl.innerHTML = '';
    var done = state.path.length;
    for (var i = 0; i < done; i++) {
      var d = document.createElement('div');
      d.className = 'gs-dot is-done';
      progressEl.appendChild(d);
    }
    if (state.currentId) {
      var cur = document.createElement('div');
      cur.className = 'gs-dot is-active';
      progressEl.appendChild(cur);
    }
    // Back is visible if either the user has answered something in this category,
    // OR they came from the piercing-type router (so back returns them there).
    if (backBtn) backBtn.style.visibility = (state.path.length > 0 || state.previousCategory) ? 'visible' : 'hidden';
  }

  function refreshOptions(stepEl) {
    if (!stepEl || !state.category) return;
    var qKey = stepEl.dataset.q;
    var optsEl = stepEl.querySelector('.gs-opts');

    // Only apply live-hiding to STRUCTURAL keys — the ones that would truly
    // break the flow if picked with 0 matches. For non-structural keys
    // (retention, gauge, dimension, metal, stone_type, stone_size...) the
    // soft-fallback engine will drop the filter and still return results,
    // so hiding is over-aggressive and can wrongly disappear real catalog
    // options (e.g. Straight Post got hidden because the strict combo
    // diamond+2mm+straight-post returned 0 pre-fallback).
    var structural = state.category.structuralKeys || {};

    if (!state.catalog) {
      // Do NOT block on the catalog. The per-collection catalog is a slow
      // (~4-5s) server render; masking structural steps behind it left the
      // shopper staring at "Loading options…" mid-quiz. Live-hiding zero-match
      // options is a refinement, not correctness — a dead-end pick is caught by
      // the soft-fallback engine at results — so show every option immediately
      // and let them keep answering while the catalog loads in the background.
      // This function re-runs the moment the catalog resolves and applies
      // live-hiding then: in the common case (e.g. Gemstone/Charm/Plain, which
      // exist in every collection) nothing changes; at worst a genuinely-empty
      // tile drops a beat later — far better than a multi-second wall.
      if (optsEl) optsEl.style.display = '';
      var staleNote = stepEl.querySelector('.gs-opts-loading');
      if (staleNote) staleNote.remove();
      return;
    }

    var loadingNote = stepEl.querySelector('.gs-opts-loading');
    if (loadingNote) loadingNote.remove();
    if (optsEl) optsEl.style.display = '';

    if (!structural[qKey]) {
      stepEl.querySelectorAll('.gs-opt').forEach(function (opt) { opt.style.display = ''; });
      if (optsEl) optsEl.style.gridTemplateColumns = '';
      return;
    }

    var strictCount = state.category.strictCount;
    var visibleCount = 0;
    stepEl.querySelectorAll('.gs-opt').forEach(function (opt) {
      var hypo = Object.assign({}, state.answers);
      // Only resolve THIS question's own key through writes (e.g. curved_bar's
      // writes.form) — not the whole writes object. Several options bundle in
      // side-effect keys purely to skip a redundant later question (gauge:'20G'
      // on lobe's charm/plain/stone-type options, has_dangle_want on Dangle);
      // those aren't meant to be live-hiding constraints, and checking them
      // against catalog data that was never curated for them (e.g. lobe
      // earrings largely lack a gauge metafield) wrongly hid Charm/Plain/
      // stone-type tiles that do have real matching products.
      var writes = opt.dataset.writes ? JSON.parse(opt.dataset.writes) : null;
      hypo[qKey] = (writes && Object.prototype.hasOwnProperty.call(writes, qKey)) ? writes[qKey] : opt.dataset.value;
      var n = strictCount(state.catalog, hypo);
      var hide = n === 0;
      opt.style.display = hide ? 'none' : '';
      if (!hide) visibleCount++;
    });
    if (!visibleCount) {
      // safety valve: don't dead-end the user — show everything
      stepEl.querySelectorAll('.gs-opt').forEach(function (opt) { opt.style.display = ''; });
      visibleCount = stepEl.querySelectorAll('.gs-opt').length;
    }
    // Avoid an orphan single-tile last row (e.g. a 4-option cols-2 grid
    // dropping to 3 live options renders 2-then-1) by tightening the grid
    // to fit exactly the visible count whenever that's fewer than the
    // configured column count. Once all options are visible again the
    // configured cols-N class takes back over.
    if (optsEl) {
      var configuredCols = parseInt((optsEl.className.match(/cols-(\d+)/) || [])[1], 10) || 4;
      optsEl.style.gridTemplateColumns = visibleCount < configuredCols
        ? 'repeat(' + visibleCount + ', 1fr)'
        : '';
    }
  }

  // Called after catalog loads: apply dynamic question tree from collection metadata if available.
  function applyDynamicQuestions(result) {
    state.catalog = result.products;
    state.collectionMeta = result.meta;

    // Only apply the dynamic builder when the current category has NO
    // hand-authored questions. Hand-crafted category configs always win —
    // otherwise a collection's `custom.collection_filters` metafield would
    // silently override our carefully-designed question tree with a
    // catalog-derived one that mixes cross-category jewelry (e.g. showing
    // 14G/16G gauges in a nose-ring quiz because seamless rings live in the
    // nose-rings collection too).
    var hasStaticQuestions = state.category && state.category.questions &&
                             Object.keys(state.category.questions).length > 0;

    if (!hasStaticQuestions &&
        result.meta && result.meta.filters && result.meta.filters.length > 0 &&
        window.FT_QUIZ_BUILD_DYNAMIC &&
        state.path.length === 0) {  // only switch trees before the user has answered anything
      var dynamic = window.FT_QUIZ_BUILD_DYNAMIC(
        result.meta.filters,
        result.meta.exclude || '',
        result.products,
        state.category
      );
      if (dynamic) {
        state.category = Object.assign({}, state.category, {
          questions:     dynamic.questions,
          initial:       dynamic.initial,
          filter:        dynamic.filter,
          strictCount:   dynamic.strictCount,
          structuralKeys: dynamic.structuralKeys || {}
        });
        // Re-start from the dynamic initial question
        state.path = []; state.answers = {}; state.answerOrder = [];
        stepsWrap.style.display = '';
        if (progressEl) progressEl.style.display = '';
        if (navEl) navEl.style.display = '';
        resultsEl.classList.remove('is-active');
        if (gridEl) gridEl.innerHTML = '';
        if (state.category.intro_helix) renderHelixCard(state.category.intro_helix);
        showQuestion(dynamic.initial);
        return;
      }
    }

    var activeStep = stepsWrap.querySelector('.gs-step');
    if (activeStep) refreshOptions(activeStep);
  }

  // Warm the catalog cache for every catalog_url an about-to-render
  // question's options carry (e.g. ear's placement picker — each placement
  // swaps to its own product pool on click). Without this, the fetch for a
  // placement only starts AFTER the customer clicks it, so every pick pays
  // the full 3-5s+ fetch latency with nothing to show but "Loading
  // options…". Firing all of them in the background the moment the
  // picker itself renders means most have already landed by the time the
  // customer actually clicks one (Ximena, 2026-08-12 — "it can't happen").
  // fetchCatalogUrl already de-dupes identical URLs and caches by URL, so
  // placements that intentionally share a collection (e.g. Helix/
  // Anti-tragus/Snug all pointing at Cartilage Earrings) only fetch once.
  // Fire-and-forget: a prefetch failure here is silently swallowed — the
  // real on-click fetch (fetchCatalogUrlRetrying) still runs normally and
  // handles retries if the customer picks that option anyway.
  function prefetchOptionCatalogs(question) {
    var optionsArr = typeof question.options === 'function'
      ? (question.options(state.answers) || [])
      : (question.options || []);
    var seen = {};
    optionsArr.forEach(function (o) {
      if (o.catalog_url && !seen[o.catalog_url]) {
        seen[o.catalog_url] = true;
        fetchCatalogUrl(o.catalog_url).catch(function () {});
      }
    });
  }

  function showQuestion(qid) {
    var q = state.category.questions[qid];
    if (!q) {
      console.error('[guided-shopping] unknown question id', qid);
      return;
    }
    state.currentId = qid;
    q.id = qid; // ensure id is on the object for buildStepDOM

    // Clear and render this single step
    stepsWrap.innerHTML = '';
    var stepEl = buildStepDOM(q);
    stepsWrap.appendChild(stepEl);
    prefetchOptionCatalogs(q);

    renderProgress();
    refreshOptions(stepEl);
    // helix.title / helix.body can be functions of state.answers, same
    // pattern as question.question — resolve without mutating q.helix
    // itself (this question object is reused every time it's visited).
    var helixContent = q.helix;
    if (helixContent && (typeof helixContent.title === 'function' || typeof helixContent.body === 'function')) {
      helixContent = {
        title: typeof helixContent.title === 'function' ? helixContent.title(state.answers) : helixContent.title,
        body:  typeof helixContent.body  === 'function' ? helixContent.body(state.answers)  : helixContent.body
      };
    }
    renderHelixCard(helixContent);
  }

  function onAnswer(question, option, optEl) {
    pushQuizEvent('quiz_question_answered', {
      quiz_category: state.category && state.category.id,
      quiz_question: question.key,
      quiz_answer: option.value
    });

    // Visual selection feedback
    var stepEl = optEl.closest('.gs-step');
    if (stepEl) {
      stepEl.querySelectorAll('.gs-opt').forEach(function (o) { o.classList.remove('is-sel'); });
      optEl.classList.add('is-sel');
    }

    // Category switch: used by the 'all' routing category to hand off to a specific quiz.
    if (option.categorySwitch) {
      // Remember where we came from so back-navigation on Q1 of the routed
      // category can return to the piercing-type picker instead of dead-ending.
      state.previousCategory = { id: state.category.id, questionId: state.currentId };
      setTimeout(function () { startQuiz(option.categorySwitch); }, 220);
      return;
    }

    // Record the answer(s).
    // If option.writes is present (schema compound-answer pattern — e.g. picking
    // "Diamond" writes ornament_type='gemstone' AND stone_type='diamond'), apply
    // each key/value pair. Otherwise fall back to question.key = option.value.
    var writes = option.writes || {};
    if (!option.writes) writes[question.key] = option.value;
    Object.keys(writes).forEach(function (k) {
      state.answers[k] = writes[k];
      if (state.answerOrder.indexOf(k) === -1) state.answerOrder.push(k);
    });
    state.path.push({ id: state.currentId, key: question.key, value: option.value, writes: writes });

    // Per-placement pool swap. An option can carry its own catalog_url — used
    // by the ear placement question so "Tragus" draws results from the Tragus
    // collection, "Conch" from Conch, etc., instead of one generic pool. Swap
    // the active pool now and load it; results are several questions away, so
    // the fetch has time to land. Clone the category (don't mutate the shared
    // CFG object) and guard the async result against a later swap / restart.
    // label swaps too (Tragus/Rook/Daith/...) — the results-page "Browse the
    // full X collection" CTA reads state.category.label, so without this it
    // kept saying "ear piercings collection" even though the href underneath
    // was already correctly pointed at the specific placement's collection.
    if (option.catalog_url) {
      var poolUrl = option.catalog_url;
      var poolLabel = option.label || state.category.label;
      state.category = Object.assign({}, state.category, { catalogUrl: poolUrl, catalog_url: poolUrl, label: poolLabel });
      state.catalog = null;
      state.collectionMeta = null;
      state.catalogPromise = fetchCatalogUrlRetrying(poolUrl);
      state.catalogPromise.then(function (result) {
        if (state.category && state.category.catalogUrl === poolUrl) {
          state.catalog = result.products;
          state.collectionMeta = result.meta;
          var activeStep = stepsWrap.querySelector('.gs-step');
          if (activeStep) refreshOptions(activeStep);
        }
      }).catch(function () {
        // Retries exhausted — unblock the UI instead of leaving every
        // structural question on "Loading options…" forever. state.catalog
        // must become non-null for refreshOptions to stop masking; an empty
        // pool at least lets the shopper see the bestseller fallback/CTA
        // instead of a dead spinner.
        if (state.category && state.category.catalogUrl === poolUrl) {
          state.catalog = [];
          state.collectionMeta = null;
          var activeStep = stepsWrap.querySelector('.gs-step');
          if (activeStep) refreshOptions(activeStep);
        }
      });
    }

    // next can be a string OR a function of state.answers. The function
    // form lets a step's next-question depend on an earlier answer (used
    // by belly ornament: hoops skip Q_belly_dimension and go straight to
    // Q_metal, while curved/floating go through the length step).
    var next = typeof option.next === 'function'
      ? option.next(state.answers)
      : option.next;
    setTimeout(function () {
      if (!next || next === 'final') {
        showResults();
      } else {
        showQuestion(next);
      }
    }, 220);
  }

  // ─── results rendering ─────────────────────────────────────────────
  // Normalize dimension strings so "5/16" (customer answer) matches
  // "5/16in", "5/16in (8mm)", '5/16"', etc. from the emitter's variant
  // records. Same shape as the filter-side normalizeDim in the config.
  function normDim(s) {
    return String(s || '')
      .toLowerCase()
      .replace(/\(.*?\)/g, '')
      .replace(/in\b/g, '')
      .replace(/["]/g, '')
      .replace(/\s+/g, '')
      .replace(/mm$/, '');
  }

  // Gauge, like dimension, needs normalizing before comparison: the catalog
  // emitter writes the variant record straight from the canonical metafield
  // format "20G (0.8mm)" while the quiz answer is the bare "20G" the option
  // writes — a raw string match never fires so gauge silently never scores.
  function normGauge(s) {
    var m = String(s || '').match(/(\d+)\s*G/i);
    return m ? m[1] + 'G' : '';
  }

  // Find the single variant that best matches the customer's answers.
  // Score: metal=8, style=4, gauge=2, length=1 (so higher-priority axes
  // dominate). The customer explicitly chose these — deep-linking to a
  // variant that matches them all is the whole point of asking.
  function pickVariant(p) {
    var vs = (p && p.variants) || [];
    if (vs.length === 0) return null;
    var a = state.answers || {};
    var wantMetal  = String(a.metal || '').toLowerCase();
    var wantStyle  = String(a.retention || '').toLowerCase();
    var wantGauge  = normGauge(a.gauge || '');
    var wantLength = normDim(a.dimension || '');

    function eq(v, want) { return want && String(v || '').toLowerCase() === want; }
    function eqDim(v, want) { return want && normDim(v) === want; }
    function eqGauge(v, want) { return want && normGauge(v) === want; }

    var best = null, bestScore = -1;
    for (var i = 0; i < vs.length; i++) {
      var v = vs[i], s = 0;
      if (eq(v.metal,  wantMetal))    s += 8;
      if (eq(v.style,  wantStyle))    s += 4;
      if (eqGauge(v.gauge, wantGauge)) s += 2;
      if (eqDim(v.length, wantLength)) s += 1;
      if (s > bestScore) { best = v; bestScore = s; }
    }
    return best;
  }

  function pickImage(p) {
    var v = pickVariant(p);
    if (v && v.img) return v.img;
    // Fall back to color-slotted images from the old catalog fields,
    // then the product's default. Keeps this resilient if variant.image
    // is unset on a specific variant.
    var m = state.answers && state.answers.metal;
    if (m === 'yellow'   && p.image_yellow)   return p.image_yellow;
    if (m === 'white'    && p.image_white)    return p.image_white;
    if (m === 'rose'     && p.image_rose)     return p.image_rose;
    if (m === 'platinum' && p.image_platinum) return p.image_platinum;
    return p.image || p.image_yellow || p.image_white || p.image_rose || p.image_platinum || '';
  }
  function pickPrice(p) {
    var v = pickVariant(p);
    if (v && typeof v.price === 'number') return v.price;
    var m = state.answers && state.answers.metal;
    if (m === 'yellow'   && p.price_yellow)   return p.price_yellow;
    if (m === 'white'    && p.price_white)    return p.price_white;
    if (m === 'rose'     && p.price_rose)     return p.price_rose;
    if (m === 'platinum' && p.price_platinum) return p.price_platinum;
    return p.price_min;
  }
  function buildUrl(p) {
    // gsq=1 marks the landing PDP view as quiz-driven in GA4's auto-collected
    // page_location — attribution that works even before/without a GTM tag for
    // the dataLayer events. Harmless to Shopify (unknown params are ignored)
    // and to SEO (PDP canonical strips query strings).
    var base = p.url || '#';
    var v = pickVariant(p);
    if (v && v.vid) return base + '?gsq=1&variant=' + v.vid;
    var m = state.answers && state.answers.metal;
    var vid = (m === 'yellow'   && p.vid_yellow)   ? p.vid_yellow
            : (m === 'white'    && p.vid_white)    ? p.vid_white
            : (m === 'rose'     && p.vid_rose)     ? p.vid_rose
            : (m === 'platinum' && p.vid_platinum) ? p.vid_platinum
            : (p.vid_yellow || p.vid_white || p.vid_rose || p.vid_platinum);
    return vid ? base + '?gsq=1&variant=' + vid : base + '?gsq=1';
  }

  // Diversify the FIRST `headCount` shown results across price bands
  // (entry/mid/premium) instead of leaving them in pure price-DESC order
  // within the existing relevance sort — a top-9 wall of near-identical
  // premium studs reads as "nothing here for me" to a price-sensitive
  // shopper even when cheaper options exist a few rows down (Ximena,
  // 2026-08-12). Bands are drawn from a modest prefix of the already
  // relevance-sorted list (not the whole catalog), so "diverse" never means
  // "less relevant" — every product in the spread was already among the
  // best matches, just reordered by price band. Anything past `headCount`
  // (or past the prefix pool) is untouched, still pure relevance order.
  function priceBandSpread(list, headCount) {
    if (!Array.isArray(list) || list.length <= headCount) return list;
    var poolSize = Math.min(list.length, Math.max(headCount * 4, 24));
    var pool = list.slice(0, poolSize);
    var rest = list.slice(poolSize);

    var prices = pool.map(function (p) { return pickPrice(p) || 0; }).sort(function (a, b) { return a - b; });
    var loCut = prices[Math.floor(prices.length / 3)];
    var hiCut = prices[Math.floor(prices.length * 2 / 3)];

    var bands = [[], [], []]; // entry, mid, premium — relevance order preserved within each
    pool.forEach(function (p) {
      var v = pickPrice(p) || 0;
      var idx = (v <= loCut) ? 0 : (v <= hiCut ? 1 : 2);
      bands[idx].push(p);
    });

    var head = [];
    var bi = [0, 0, 0];
    while (head.length < headCount && (bi[0] < bands[0].length || bi[1] < bands[1].length || bi[2] < bands[2].length)) {
      for (var b = 0; b < 3 && head.length < headCount; b++) {
        if (bi[b] < bands[b].length) { head.push(bands[b][bi[b]]); bi[b]++; }
      }
    }
    var leftoverPool = pool.filter(function (p) { return head.indexOf(p) === -1; });
    return head.concat(leftoverPool, rest);
  }

  // Give recently-added products a guaranteed, prominent (but not rigidly
  // fixed) spot near the top of the shown results — Ximena wants new
  // arrivals to "dominate" visually, but only ever among products that
  // already matched the customer's answers. Runs AFTER priceBandSpread, as
  // a final layer: pulls up to 2 items created within the last 90 days from
  // the same relevance-sorted prefix priceBandSpread already drew from, and
  // splices them into the head at staggered (not identical, not position-0)
  // slots — position 0 stays whatever the relevance sort + price spread
  // already decided was the single best match, so "new" never outranks
  // "best match" outright, it just gets folded in near the top. If nothing
  // in the matched set is new, this is a complete no-op — never reaches
  // outside the matched pool to force an unrelated item in.
  var NEW_ARRIVAL_DAYS = 90;
  function newArrivalsBoost(list, headCount) {
    if (!Array.isArray(list) || !list.length) return list;
    var cutoff = (Date.now() / 1000) - (NEW_ARRIVAL_DAYS * 86400);
    var poolSize = Math.min(list.length, Math.max(headCount * 4, 24));
    var pool = list.slice(0, poolSize);
    var rest = list.slice(poolSize);

    var head = pool.slice(0, headCount);
    var headIds = {};
    head.forEach(function (p) { headIds[p.id] = true; });

    var newOnes = [];
    for (var i = 0; i < pool.length && newOnes.length < 2; i++) {
      var p = pool[i];
      if (typeof p.created_at === 'number' && p.created_at >= cutoff && !headIds[p.id]) {
        newOnes.push(p);
      }
    }
    if (!newOnes.length) return list;

    newOnes.forEach(function (p, i) {
      var pos = Math.min(head.length, 1 + i * 2); // staggered near-top slots, never position 0
      head.splice(pos, 0, p);
    });
    head = head.slice(0, headCount);

    var leftoverPool = pool.filter(function (p) { return head.indexOf(p) === -1; });
    return head.concat(leftoverPool, rest);
  }

  function renderCard(p) {
    var src = pickImage(p);
    var img = src ? '<img src="' + src + '" alt="' + String(p.title || '').replace(/"/g, '&quot;') + '" loading="lazy">' : '';
    var badge = p.is_bestseller ? '<span class="gs-card-badge">Bestseller</span>' : '';
    var price = fmtPrice(pickPrice(p));
    var href = buildUrl(p);
    return (
      '<a class="gs-card" href="' + href + '" data-product-id="' + p.id + '" data-product-title="' + String(p.title || '').replace(/"/g, '&quot;') + '">' +
        '<div class="gs-card-img">' + img + badge + '</div>' +
        '<div class="gs-card-title">' + p.title + '</div>' +
        '<div class="gs-card-price">' + price + '</div>' +
      '</a>'
    );
  }


  function showResults() {
    // If a placement swapped the pool and its fetch hasn't landed yet, wait for
    // it rather than rendering an empty (or stale) result set. Re-enter once it
    // resolves, unless another swap/restart replaced the pending promise.
    if (!state.catalog && state.catalogPromise) {
      var pending = state.catalogPromise;
      if (countEl) countEl.textContent = 'Finding your matches…';
      resultsEl.classList.add('is-active');
      stepsWrap.style.display = 'none';
      if (progressEl) progressEl.style.display = 'none';
      if (navEl) navEl.style.display = 'none';
      pending.then(function () { if (pending === state.catalogPromise) showResults(); })
             .catch(function () {});
      return;
    }

    var matched = state.category.filter(state.catalog || [], state.answers, state.answerOrder);
    var dropped = (matched && matched._droppedFilters) ? matched._droppedFilters : [];

    // Zero-results safety net: if the soft-fallback drop loop couldn't
    // find ANY matches (rare — means every non-structural filter got
    // dropped and there's still nothing), show top bestsellers from the
    // category instead of a dead-end empty state. A category page with
    // "no results, sorry" is a bounce; showing bestsellers gives the
    // customer somewhere to click.
    var usedBestsellerFallback = false;
    if (matched.length === 0 && Array.isArray(state.catalog) && state.catalog.length > 0) {
      var pool = state.catalog.slice();
      // Never violate the physical SHAPE the customer chose — a "Ring" pick must
      // never fall back to studs (or vice versa). Restrict the bestseller pool to
      // the chosen form when products of that form exist; only if none exist do we
      // widen to the whole catalog (something beats a dead end). Ornament/stone can
      // relax here — "no diamond rings in stock, here are our best rings" reads fine;
      // "here are some studs" does not.
      var formPick = state.answers.form;
      if (formPick) {
        var byForm = pool.filter(function (p) { return p.form === formPick; });
        if (byForm.length > 0) pool = byForm;
      }
      pool.sort(function (a, b) {
        var ar = (typeof a.sales_rank === 'number') ? a.sales_rank : null;
        var br = (typeof b.sales_rank === 'number') ? b.sales_rank : null;
        if (ar !== null && br !== null) return ar - br;
        if (ar !== null) return -1;
        if (br !== null) return 1;
        if (a.is_bestseller !== b.is_bestseller) return a.is_bestseller ? -1 : 1;
        return b.price_min - a.price_min;
      });
      matched = newArrivalsBoost(priceBandSpread(pool, 9), 9).slice(0, 9);
      usedBestsellerFallback = true;
    }
    var shown = newArrivalsBoost(priceBandSpread(matched, 9), 9).slice(0, 24);

    pushQuizEvent('quiz_complete', {
      quiz_category: state.category && state.category.id,
      quiz_result_count: matched.length,
      quiz_used_fallback: usedBestsellerFallback
    });

    stepsWrap.style.display  = 'none';
    if (progressEl) progressEl.style.display = 'none';
    if (navEl) navEl.style.display = 'none';
    resultsEl.classList.add('is-active');

    var countText = usedBestsellerFallback
      ? 'No exact matches — here are our top ' + (state.category.label || '').toLowerCase()
      : matched.length + ' style' + (matched.length !== 1 ? 's' : '') + ' found for you';

    // Banners: soft-fallback wins over bestseller-fallback (only one shows).
    // Bestseller-fallback fires only when nothing was matched. The old
    // gold-color merchandising note ("All 14K gold styles are available in
    // yellow, white, and rose gold") was removed 2026-07-14 — redundant now
    // that Q_metal is asked in every flow, so the shopper has already made
    // their color choice by the time they see results.
    if (usedBestsellerFallback) {
      countEl.innerHTML = countText +
        '<div class="gs-relax-banner">We couldn’t find a piece matching every one of your picks. Below are our current bestsellers — or hit <b>Refine</b> to loosen an answer.</div>';
    } else if (dropped.length && matched.length) {
      // Per Ximena (2026-08-11): don't call out which specific fields got
      // relaxed (gauge/length/metal/etc) - the set varies by category and
      // naming it read as narrower/more technical than intended. Generic
      // "broadened your results" framing instead.
      countEl.innerHTML = countText +
        '<div class="gs-relax-banner">We’ve broadened your results to show more options. Hit <b>Refine</b> to narrow again.</div>';
    } else {
      countEl.textContent = countText;
    }

    gridEl.innerHTML = shown.length
      ? shown.map(renderCard).join('')
      : '<div class="gs-empty"><p>No products matched your selections.</p><small>Try starting over with different choices.</small></div>';

    // Collection CTA — always visible below the grid. If a customer doesn't
    // love the picks (or we returned zero), they can escape to the full
    // collection instead of just seeing "start over". Uses the current
    // category's catalogUrl minus the ?view=quiz-catalog query.
    var ctaEl = document.getElementById('gs-collection-cta');
    if (ctaEl) {
      var raw = (state.category && (state.category.catalogUrl || state.category.catalog_url)) || '';
      var collectionUrl = raw.split('?')[0];
      var label = (state.category && state.category.label) || 'collection';
      var lower = label.toLowerCase();
      ctaEl.innerHTML = collectionUrl
        ? '<p class="gs-collection-cta-copy">Don’t see something you love?</p>' +
          '<a class="gs-collection-cta-btn" href="' + collectionUrl + '">Browse the full ' + lower + ' collection &rarr;</a>'
        : '';
    }

    renderHelixCard({
      title: 'Your matches',
      body: 'Tap any piece to view it in detail. Still unsure? Start over and try a different combo, ' +
            'or browse the full collection from the navigation.'
    });
  }

  // ─── helix ─────────────────────────────────────────────────────────
  function renderHelixCard(content) {
    if (!helixCard || !content) return;
    helixCard.innerHTML =
      '<div class="gs-helix__card-title">' + (content.title || '') + '</div>' +
      '<div class="gs-helix__card-body">' + (content.body || '') + '</div>';
    if (helixBarPanel) {
      helixBarPanel.innerHTML =
        '<h4>' + (content.title || '') + '</h4>' +
        '<p>' + (content.body || '') + '</p>';
    }
  }

  // ─── quiz lifecycle ────────────────────────────────────────────────
  function startQuiz(categoryId) {
    var cat = CFG[categoryId];
    if (!cat) {
      console.error('[guided-shopping] unknown category', categoryId);
      return;
    }
    state.category = cat;
    state.currentId = null;
    state.path = [];
    state.answers = {};
    // NOTE: previousCategory intentionally NOT cleared here — set only via
    // onAnswer(categorySwitch) and cleared on back-nav or explicit resetState.
    state.answerOrder = [];
    state.catalog = null;
    state.catalogPromise = null;

    quizTitleEl.textContent = 'Find Your Perfect Piece';
    quizSubEl.textContent   = cat.intro;

    // Reset UI
    stepsWrap.style.display  = '';
    if (progressEl) progressEl.style.display = '';
    if (navEl) navEl.style.display = '';
    resultsEl.classList.remove('is-active');
    if (gridEl) gridEl.innerHTML = '';

    if (helixName) helixName.textContent = 'Ruby';
    if (helixRole) helixRole.textContent = 'FreshTrends Piercing Advisor';
    if (cat.intro_helix) renderHelixCard(cat.intro_helix);

    // Render first question (will overwrite the intro helix card)
    showQuestion(cat.initial);

    // Load catalog lazily; once in, apply dynamic questions or refresh visibility.
    // Categories flagged deferCatalog (ear) don't load a pool here — their
    // placement question swaps in a per-piercing collection on the first answer,
    // so auto-loading a default pool now would only race that swap.
    var cat = state.category; // capture before async
    if (!cat.deferCatalog) {
      loadCatalog(cat).then(function (result) {
        applyDynamicQuestions(result);
      }).catch(function () {
        // Retries (see fetchCatalogUrlRetrying) exhausted. state.catalog was
        // never set to anything on this path, which left every structural
        // question stuck on "Loading options…" forever, not just "results
        // will be empty" as this comment used to (wrongly) claim - set an
        // empty pool so refreshOptions actually unblocks.
        if (state.category === cat) {
          state.catalog = [];
          state.collectionMeta = null;
          var activeStep = stepsWrap.querySelector('.gs-step');
          if (activeStep) refreshOptions(activeStep);
        }
      });
    }
  }

  // ─── modal open/close ──────────────────────────────────────────────
  function openModal(categoryId) {
    modal.classList.add('is-open');
    document.body.classList.add('gs-modal-locked');
    startQuiz(categoryId);
  }
  function closeModal() {
    modal.classList.remove('is-open');
    document.body.classList.remove('gs-modal-locked');
    if (helixBar) helixBar.classList.remove('is-open');
    resetState();
  }

  // ─── wire up triggers ──────────────────────────────────────────────
  // Source label per entry point, for the quiz_open event — lets GA4 answer
  // "which placement actually drives quiz starts" instead of just a single
  // undifferentiated click count (Ximena, 2026-08-13).
  function triggerSource(btn) {
    if (btn.closest('#gs-collection-mobile-trigger')) return 'collection_mobile';
    if (btn.closest('.sidenav__quiz-item')) return 'mobile_menu';
    if (btn.closest('.header__item-quiz-btn')) return 'desktop_nav';
    if (btn.closest('.gs-banner')) return 'homepage_banner';
    if (btn.classList.contains('gs-float-btn')) return 'floating';
    return 'other';
  }
  document.querySelectorAll('[data-gs-trigger]').forEach(function (btn) {
    btn.addEventListener('click', function () {
      pushQuizEvent('quiz_open', { quiz_source: triggerSource(btn), quiz_category: btn.dataset.gsTrigger });
      openModal(btn.dataset.gsTrigger);
    });
  });

  // ─── deep-link open (e.g. from an email/newsletter) ────────────────
  // ?quiz=<category> on any page opens the quiz on load. ?quiz=all (or a bare
  // ?quiz) opens the general "what type of piercing?" picker — same as the
  // homepage banner. A specific category (nose, ear, belly, lip, …) opens that
  // quiz directly. Unknown values are ignored so a stray param can't break the
  // page. Runs after trigger wiring, so the modal DOM is present.
  try {
    var _qp = new URLSearchParams(window.location.search);
    if (_qp.has('quiz')) {
      var _qcat = _qp.get('quiz') || 'all';
      if (CFG[_qcat]) {
        pushQuizEvent('quiz_open', { quiz_source: 'url_param', quiz_category: _qcat });
        openModal(_qcat);
      }
    }
  } catch (e) {}

  // Delegated (gridEl's innerHTML is rebuilt on every showResults() call, so
  // per-card listeners would need re-binding each time — one listener on the
  // stable grid container handles every render instead).
  if (gridEl) {
    gridEl.addEventListener('click', function (e) {
      var card = e.target.closest('.gs-card');
      if (!card) return;
      pushQuizEvent('quiz_result_click', {
        quiz_category: state.category && state.category.id,
        product_id: card.dataset.productId,
        product_title: card.dataset.productTitle
      });
    });
  }

  // ─── quiz-assisted order attribution ────────────────────────────────
  // Landing on any page with ?gsq=1 (only quiz result cards emit that param)
  // stamps the Shopify cart with a quiz_assisted attribute. Cart attributes
  // ride into the order at checkout, so "orders that came through the quiz"
  // is countable straight from Shopify order data — the purchase-side half of
  // quiz conversion reporting that GA4 events alone can't provide (Paul,
  // 2026-08-16). Stamped once per session; fire-and-forget, can't break the
  // page. The daily quiz report reads this via order customAttributes.
  try {
    if (new URLSearchParams(window.location.search).get('gsq') === '1' &&
        sessionStorage.getItem('gs_attr_stamped') !== '1') {
      sessionStorage.setItem('gs_attr_stamped', '1');
      fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ attributes: { quiz_assisted: 'true' } })
      }).catch(function () {});
    }
  } catch (e) { /* attribution must never break the page */ }

  // ─── floating trigger dismiss (collection pages) ────────────────────
  // Hide for the rest of this browsing session once dismissed — sessionStorage,
  // not localStorage, so it comes back on the shopper's next visit instead of
  // staying hidden forever (Ximena, 2026-08-13).
  (function () {
    var floatWrap = document.getElementById('gs-float-wrap');
    var dismissBtn = document.getElementById('gs-float-dismiss');
    if (!floatWrap) return;
    try {
      if (sessionStorage.getItem('gs_float_dismissed') === '1') {
        floatWrap.style.display = 'none';
        return;
      }
    } catch (e) { /* storage unavailable — leave it visible */ }
    if (dismissBtn) {
      dismissBtn.addEventListener('click', function (e) {
        e.stopPropagation();
        floatWrap.style.display = 'none';
        try { sessionStorage.setItem('gs_float_dismissed', '1'); } catch (e) {}
      });
    }
  })();

  // ─── mobile collection-page trigger: reposition after the subcollection
  // tiles ── The hero + subcollection tiles + grid render from one combined
  // section (collection-template) with no Liquid seam to inject a new
  // section between them, so this moves the trigger's DOM node into place
  // client-side instead. Mobile only — desktop uses the header nav button
  // (Ximena, 2026-08-13). Anchored to the subcollection row rather than the
  // hero banner itself — moved lower per Ximena's follow-up; falls back to
  // the hero banner on collections with no subcollection row.
  (function () {
    if (window.innerWidth > 768) return;
    var trigger = document.getElementById('gs-collection-mobile-trigger');
    if (!trigger) return;
    var anchor = document.querySelector('.collection-page__wrapper-subcollections')
      || document.querySelector('.collection-banner-wrapper');
    if (!anchor || !anchor.parentNode) return;
    anchor.insertAdjacentElement('afterend', trigger);
  })();

  if (closeBtn) closeBtn.addEventListener('click', closeModal);

  modal.addEventListener('click', function (e) {
    if (e.target === modal) closeModal();
  });

  document.addEventListener('keydown', function (e) {
    if (e.key === 'Escape' && modal.classList.contains('is-open')) closeModal();
  });

  // Back button: pop the last answer from the path and re-render that question.
  // On Q1 of a routed-into category, return to the piercing-type picker.
  if (backBtn) {
    backBtn.addEventListener('click', function () {
      if (state.path.length === 0) {
        if (state.previousCategory) {
          var prev = state.previousCategory;
          state.previousCategory = null;
          startQuiz(prev.id);
        }
        return;
      }
      var last = state.path.pop();
      // Remove all writes that this step applied (supports schema compound answers)
      var writes = last.writes || {};
      if (!last.writes) writes[last.key] = last.value;
      Object.keys(writes).forEach(function (k) {
        delete state.answers[k];
        var oi = state.answerOrder.indexOf(k);
        if (oi !== -1) state.answerOrder.splice(oi, 1);
      });
      showQuestion(last.id);
    });
  }

  if (restartBtn) {
    restartBtn.addEventListener('click', function () {
      var catId = state.category ? state.category.id : 'nose';
      startQuiz(catId);
    });
  }

  // "← Refine" on the results page — go back one question
  if (resultsBackBtn) {
    resultsBackBtn.addEventListener('click', function () {
      if (state.path.length === 0) return;
      var last = state.path.pop();
      // Remove all writes this step applied (schema compound answers)
      var writes = last.writes || {};
      if (!last.writes) writes[last.key] = last.value;
      Object.keys(writes).forEach(function (k) {
        delete state.answers[k];
        var oi = state.answerOrder.indexOf(k);
        if (oi !== -1) state.answerOrder.splice(oi, 1);
      });
      resultsEl.classList.remove('is-active');
      stepsWrap.style.display = '';
      if (progressEl) progressEl.style.display = '';
      if (navEl) navEl.style.display = '';
      showQuestion(last.id);
    });
  }

  if (helixBarBtn && helixBar) {
    helixBarBtn.addEventListener('click', function () {
      helixBar.classList.toggle('is-open');
    });
  }
})();
